import { css } from '@emotion/css';

import { mobileMediaQuery, nonMobileMediaQuery } from '../../../constants';
import { h2Css, m, p1Css } from '../../../motif';

export const cardContainerCss = css`
  background: ${m('--neutral-v0')};
  border-radius: ${m('--spacing-m')};
  display: flex;
  flex-direction: column;
  gap: ${m('--spacing-m')};
  height: 100%;
  padding: ${m('--spacing-m')};
  position: relative;

  ${mobileMediaQuery} {
    box-shadow: 0 -8px 16px 0 rgba(0, 0, 0, 0.12);
  }
`;

export const blackThemeCss = css`
  background: ${m('--neutral-v625')};
  color: ${m('--fg-color')};
`;

export const cardDropShadowCss = css`
  box-shadow: ${m('--box-shadow-m')};
`;

export const cardContainerClickableCss = css`
  color: inherit;
  text-decoration: none;
  transition: box-shadow 0.2s linear;

  svg {
    transition: fill 0.2s linear;
  }

  @media (hover: hover) {
    &:hover {
      box-shadow: ${m('--box-shadow-m')};
    }

    &:hover svg {
      fill: ${m('--action-text-hover-color')};
    }

    .${blackThemeCss} &:hover svg {
      fill: ${m('--action-text-active-color')};
    }
  }
`;

export const mediaContainerCss = css`
  border-radius: ${m('--border-radius-m')};
  flex: 0 0 auto;
  overflow: hidden;
  position: relative;
  width: 100%;
`;

export const image169Css = css`
  aspect-ratio: 16 / 9;
`;
export const image32Css = css`
  aspect-ratio: 3 / 2;
`;
export const image11Css = css`
  aspect-ratio: 1 / 1;
`;

export const mediaElementCss = css`
  height: 100%;

  > div {
    display: block;
    height: 100%;

    /* Override SnapchatEmbed styles */
    max-width: 100%;
    min-width: 100%;
    width: 100%;
  }

  img,
  video {
    display: block;
    height: 100%;
    object-fit: cover;
    object-position: center;
    width: 100%;
  }
`;

export const contentContainerCss = css`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: ${m('--spacing-xs')};
  padding-block: 0 ${m('--spacing-xs')};
  padding-inline: ${m('--spacing-xs')};

  ${nonMobileMediaQuery} {
    padding-block: ${m('--spacing-xs')};
  }
`;

export const titleContainerCss = css`
  display: flex;
  padding-block-start: ${m('--spacing-xxxs')};
  gap: ${m('--spacing-xs')};
`;

export const titleCss = css`
  &&& {
    ${h2Css}
  }

  flex: 1;
  margin: 0;
  text-align: start;
`;

export const arrowIconCss = css`
  flex-shrink: 0;
  width: 24px;
  height: 24px;
  margin-block-start: 5px;

  ${nonMobileMediaQuery} {
    margin-block-start: 9px;
  }

  @media (hover: hover) {
    fill: ${m('--action-text-default-color')};
  }

  /*For screens that do not support hover use the active (full) color */
  @media (hover: none) {
    fill: ${m('--action-text-active-color')};
  }

  [dir='rtl'] & {
    transform: scaleX(-1);
  }
`;

export const bodyCss = css`
  ${p1Css}
  --list-icon-invert: 1;

  ul,
  ol {
    display: flex;
    flex-direction: column;
    gap: ${m('--spacing-s')};
    list-style: none;
    margin: ${m('--spacing-m')} 0 0 0;
    padding: 0;
  }

  li {
    padding-inline-start: 32px;
    position: relative;

    &::before {
      background-image: url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjQiIGhlaWdodD0iMjQiIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHBhdGggZD0iTTE0Ljg0MjUgOS4yMTc1TDExLjAwMjUgMTMuMDU3NUMxMC45Mjc1IDEzLjEzMjUgMTAuODA3NSAxMy4xMzI1IDEwLjc0IDEzLjA1NzVMOS4xNSAxMS40Njc1QzguODU3NSAxMS4xNzUgOC4zODUgMTEuMTc1IDguMDkyNSAxMS40Njc1QzcuOCAxMS43NiA3LjggMTIuMjMyNSA4LjA5MjUgMTIuNTI1TDEwLjM0MjUgMTQuNzc1QzEwLjQ5MjUgMTQuOTI1IDEwLjY4IDE0Ljk5MjUgMTAuODc1IDE0Ljk5MjVDMTEuMDcgMTQuOTkyNSAxMS4yNTc1IDE0LjkxNzUgMTEuNDA3NSAxNC43NzVMMTUuOTA3NSAxMC4yNzVDMTYuMiA5Ljk4MjUgMTYuMiA5LjUxIDE1LjkwNzUgOS4yMTc1QzE1LjYxNSA4LjkyNSAxNS4xMzUgOC45MjUgMTQuODQyNSA5LjIxNzVaIiBmaWxsPSJ3aGl0ZSIvPgo8cGF0aCBmaWxsLXJ1bGU9ImV2ZW5vZGQiIGNsaXAtcnVsZT0iZXZlbm9kZCIgZD0iTTEyIDMuNzVDNy40NDc1IDMuNzUgMy43NSA3LjQ0NzUgMy43NSAxMkMzLjc1IDE2LjU1MjUgNy40NDc1IDIwLjI1IDEyIDIwLjI1QzE2LjU1MjUgMjAuMjUgMjAuMjUgMTYuNTUyNSAyMC4yNSAxMkMyMC4yNSA3LjQ0NzUgMTYuNTUyNSAzLjc1IDEyIDMuNzVaTTEyIDE4Ljc1QzguMjggMTguNzUgNS4yNSAxNS43MiA1LjI1IDEyQzUuMjUgOC4yOCA4LjI4IDUuMjUgMTIgNS4yNUMxNS43MiA1LjI1IDE4Ljc1IDguMjggMTguNzUgMTJDMTguNzUgMTUuNzIgMTUuNzIgMTguNzUgMTIgMTguNzVaIiBmaWxsPSJ3aGl0ZSIvPgo8L3N2Zz4K');
      background-repeat: no-repeat;
      background-size: contain;
      content: '';
      filter: invert(1);
      height: 24px;
      inset-inline-start: 0;
      position: absolute;
      top: 4px;
      width: 24px;
    }
  }
`;

export const blackIconCss = css`
  li {
    &::before {
      filter: invert(0);
    }
  }
`;
